import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes, msalInstance, waitForMsalAccount } from '@/auth/msalConfig';
import { SessionExpiredError } from '@/auth/errors';
import { notifySessionExpired } from '@/auth/sessionExpiredBus';
import {
  getFrontlineToken,
  isFrontlineTokenExpired,
  forceFrontlineRelogin,
} from '@/auth/frontlineSession';
import TeamsSDK from '@/teams/TeamsSDK';
import type {
  FundamentalCatalogResponse,
  FundamentalCatalogItem,
  DailyHabitCatalogItem,
  AttitudeBehaviorCatalogItem,
  SurveyListResponse,
  PagedSurveyResponse,
  PagedSurveyDto,
  SurveyItem,
  PendingInstancesResponse,
  SubmitResponseResult,
  CreateSurveyRequest,
  CreateSurveyResponse,
  TranslateTextsRequest,
  TranslateTextsResponse,
  AnalyticsSummaryResponse,
  FundamentalsResponse,
  HeatmapResponse,
  OnaResponse,
  GenerationalResponse,
  GenerationalParticipantsResponse,
  BiasResponse,
  OmissionsResponse,
  PersonalResponse,
  PersonalGrowthResponse,
  MyStatsResponse,
  AnsweredInstance,
  TipsResponse,
  TipItem,
  ActionsResponse,
  ActionItem,
  UserListResponse,
  UserItem,
  AdminPermissions,
  OuScope,
  UserModules,
  OUListResponse,
  OUItem,
  OUTreeResponse,
  OURootsResponse,
  OuCatalogCacheStatusDto,
  JobsListResponse,
  JobRunsListResponse,
  JobRunDto,
  JobSlug,
  RulesListResponse,
  RuleItem,
  NotificationSettingsDto,
  SendGeneralNotificationRequest,
  SendGeneralNotificationResult,
  CreateUserApiRequest,
  SurveyExportData,
  TraceQueryParams,
  TraceResponse,
} from './types';

// Driven by VITE_USE_MOCK in .env.development — true = use mock data, false = real APIs
export const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

import { IS_DEMO_MODE } from '@/config/featureFlags';

import { pushDebugEntry } from '@/debug/debugStore';

function _safeParseBody(body: BodyInit | null | undefined): unknown {
  if (typeof body !== 'string') return null;
  try { return JSON.parse(body); } catch { return body; }
}

const BASE_URLS = {
  survey:       import.meta.env.VITE_API_SURVEY_URL       ?? '',
  analytics:    import.meta.env.VITE_API_ANALYTICS_URL    ?? '',
  userAdmin:    import.meta.env.VITE_API_USERS_URL         ?? '',
  rules:        import.meta.env.VITE_API_RULES_URL         ?? '',
  meeting:      import.meta.env.VITE_API_MEETING_URL       ?? '',
  notifications: import.meta.env.VITE_API_NOTIFICATIONS_URL ?? '',
};

// Reexportado por compatibilidad — el código y los tests existentes lo importan desde aquí.
// Definición real en `@/auth/errors` (compartida con `auth/acquireToken.ts`, el otro cliente de API).
export { SessionExpiredError } from '@/auth/errors';

async function getToken(): Promise<string> {
  // RF-002 — Sesión Frontline (JWT propio) PRIMERO. Estos colaboradores no existen en Entra ID, así
  // que ni MSAL ni Teams SSO les darán token nunca: sin esta rama este cliente mandaba
  // `Authorization: Bearer ` VACÍO y las 9 superficies que expone respondían 401. Medido en QA:
  // `GET /v1/catalog/fundamentals` y `GET /v1/organizational-units` pasaban de 401 a 200 sólo con
  // adjuntar este token. (El catálogo caído es lo que dejaba Brillamos en blanco: `funds = []`.)
  //
  // Mismo orden de resolución que el cliente de Reconocimiento (`api/recognition/client.ts`,
  // `resolveAuthToken`), que ya lo hacía bien. Es ADITIVO: sin sesión Frontline el flujo cae a las
  // ramas de siempre y para un usuario de Azure AD no cambia absolutamente nada.
  const frontlineToken = getFrontlineToken();
  if (frontlineToken) {
    // Token vencido: forzar re-login. NO se puede caer a MSAL — el Frontline no está en Entra y
    // acabaría otra vez en `Bearer ` vacío, con el mismo 401 silencioso que este arreglo elimina.
    if (isFrontlineTokenExpired()) {
      forceFrontlineRelogin();
      return '';
    }
    return frontlineToken;
  }

  // ⚠️ A propósito, la reacción a un 401 de respuesta se limita a cuando el token está
  // verificablemente caducado (`isFrontlineTokenExpired`, detección local que no depende del
  // servicio) — igual que hace hoy el cliente de Reconocimiento. Este cliente habla con SEIS
  // servicios y `RulesEngine`/`MeetingService` no validan el issuer `pmp-frontline` (no tienen
  // `SigmaAuthenticationExtensions`): responden 401 a cualquier token Frontline, y cerrar sesión ante
  // ese 401 expulsaría al usuario al login en BUCLE.

  // Dentro de Teams: SSO silencioso (getAuthToken). El token (aud=a2d1655c) lo aceptan las APIs
  // directo, sin OBO, porque comparten audiencia. No usa redirect (que se bloquea en el iframe).
  if (TeamsSDK.isInTeams()) {
    try {
      return await TeamsSDK.getSsoToken();
    } catch (e) {
      // Visible SIEMPRE (no solo en DEV) para diagnosticar por qué Teams SSO no da token.
      console.error('[Auth] Teams SSO getAuthToken falló:', e);
      return '';
    }
  }
  // Navegador standalone: MSAL con la instancia COMPARTIDA (ya inicializada + redirect
  // procesado en AuthProvider). Crear una nueva instancia aquí no veía la sesión.
  // Espera acotada a que AuthProvider fije la cuenta: sin esto la primera tanda de queries salía
  // sin token → 401 (ver waitForMsalAccount).
  const account = await waitForMsalAccount();
  if (!account) {
    console.warn('[Auth] MSAL sin cuenta activa todavía — token vacío (401 esperado hasta que complete el login)');
    return '';
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account });
    return result.accessToken;
  } catch (e) {
    console.error('[Auth] acquireTokenSilent falló:', e);
    // Bug fila 100 (UAT 2026-08-25) — Entra ID exige reautenticación interactiva (MFA/Conditional
    // Access) y el iframe silencioso de MSAL no puede completarla (sandboxed, sin
    // allow-top-navigation). Antes esto caía al `return ''` genérico de abajo: cada endpoint
    // fallaba con 401 sin ninguna pista de que la causa era la sesión, no la acción que se hacía.
    if (e instanceof InteractionRequiredAuthError) {
      // Primer intento: recuperar en un popup, sin perder el formulario que el admin tenía a medio
      // llenar (a diferencia de un redirect, que navega la página entera y lo pierde). Si el popup
      // falla — bloqueado por el navegador, el usuario lo cierra, política corporativa, etc. — se
      // cae al SessionExpiredError de abajo, que ya tienen las 4 pantallas de traducir.
      try {
        const popupResult = await msalInstance.acquireTokenPopup({ scopes: apiScopes, account });
        return popupResult.accessToken;
      } catch (popupError) {
        // datadog-disable-next-line typescript-best-practices/no-console
        // Deliberado, no un debug leftover: única pista visible en producción de por qué la sesión
        // no se pudo recuperar (bug fila 100, UAT 2026-08-25). El propio eslint del proyecto ya
        // permite console.error (`eslint.config.js`, no-console allow: ['warn','error']).
        console.error('[Auth] acquireTokenPopup (recuperación) falló:', popupError);
        // Red de seguridad para las pantallas que NO manejan SessionExpiredError explícitamente:
        // un banner global (montado una vez en AppRouter) avisa igual.
        notifySessionExpired();
        throw new SessionExpiredError();
      }
    }
    return '';
  }
}

function getDevRole(): string {
  try {
    const stored = localStorage.getItem('sigma-demo-user');
    if (!stored) return 'Employee';
    const parsed = JSON.parse(stored) as { role?: string };
    return parsed.role ?? 'Employee';
  } catch {
    return 'Employee';
  }
}

async function request<T>(
  service: keyof typeof BASE_URLS,
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getToken();
  const correlationId = crypto.randomUUID();

  // ── DEV: capture for DebugPanel ────────────────────────────────────────────
  const _isDbg   = IS_DEMO_MODE;
  const _startMs = _isDbg ? performance.now() : 0;
  const _method  = (options.method ?? 'GET').toUpperCase();
  const _reqBody = _isDbg ? _safeParseBody(options.body) : null;
  const _url     = `${BASE_URLS[service]}${path}`;

  let res: Response;
  try {
    res = await fetch(_url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Correlation-Id': correlationId,
        // In dev, tell the backend which role the demo user has selected.
        // DevAuthMiddleware reads this and sets claims accordingly.
        ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
        ...(options.headers ?? {}),
      },
    });
  } catch (networkErr) {
    if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
      service, path, url: _url, status: null, durationMs: Math.round(performance.now() - _startMs),
      requestBody: _reqBody, responseBody: String(networkErr), isError: true });
    throw networkErr;
  }
  // ───────────────────────────────────────────────────────────────────────────

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
      service, path, url: _url, status: res.status, durationMs: Math.round(performance.now() - _startMs),
      requestBody: _reqBody, responseBody: body, isError: true });
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) {
    if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
      service, path, url: _url, status: 204, durationMs: Math.round(performance.now() - _startMs),
      requestBody: _reqBody, responseBody: null, isError: false });
    return undefined as unknown as T;
  }

  const data = await res.json() as T;
  if (_isDbg) pushDebugEntry({ id: correlationId, timestamp: Date.now(), method: _method,
    service, path, url: _url, status: res.status, durationMs: Math.round(performance.now() - _startMs),
    requestBody: _reqBody, responseBody: data, isError: false });
  return data;
}

// ── Forensics (herramienta personal, ruta oculta /_forensics) ────────────────
// Espejo TS de los DTO de MeetingService: ForensicsResponseDto + PipelineTraceDto.
// Serialización camelCase por defecto de ASP.NET Core.
export interface ForensicsStage {
  order: number; name: string; service: string; timestamp: string | null;
  outcome: string; summary: string; details: string[];
}
export interface ForensicsParticipant {
  userId: string; displayName: string | null; email: string | null; ouId: string | null;
  timezone: string; surveysToday: number; surveysThisWeek: number;
  hoursSinceLastEligibleMeeting: number | null;
  isEligible: boolean; reason: string | null;
  includedInSample: boolean; samplingReason: string | null;
}
export interface ForensicsInstance {
  id: string; surveyName: string; evaluatorId: string; status: string;
  createdAt: string; expiresAt: string; respondedAt: string | null;
}
export interface ForensicsNotification {
  id: string; channel: string; notificationType: string; recipientId: string;
  recipientEmail: string; status: string; createdAt: string; sentAt: string | null;
  errorMessage: string | null; surveyInstanceId: string | null;
}
export interface ForensicsLog {
  timestamp: string | null; service: string; level: string | null;
  message: string | null; exception: string | null;
}
export interface ForensicsMeeting {
  id: string; subject: string; organizerId: string; tenantId: string; status: string;
  startedAt: string | null; endedAt: string | null; durationMinutes: number | null;
  correlationId: string | null; isRecurring: boolean;
  attendanceReportAppliedAt: string | null; attendanceReportAttempts: number;
  calendarMatchedExactly: boolean | null;
}
export interface ForensicsMatch {
  meetingId: string; subject: string; organizerId: string; status: string;
  startedAt: string | null; endedAt: string | null;
}
export interface PipelineTrace {
  meeting: ForensicsMeeting; stages: ForensicsStage[]; participants: ForensicsParticipant[];
  surveyInstances: ForensicsInstance[]; notifications: ForensicsNotification[];
  rawLogs: ForensicsLog[]; warnings: string[]; highlightUserId: string | null;
}
export interface ForensicsRule {
  code: string; description: string; value: string; valueType: string;
  defaultValue: string; isActive: boolean;
}
export interface ForensicsResponse {
  trace: { trace: PipelineTrace | null; matches: ForensicsMatch[]; notFoundReason: string | null };
  allowlist: { enabled: boolean; allowedEmails: string[] } | null;
  rules: ForensicsRule[];
}

export interface LiveParticipant {
  userId: string; displayName: string | null; email: string | null;
  agentConnected: boolean; joinedEarly: boolean;
}
export interface LiveSession {
  meetingId: string; correlationId: string | null; subject: string; status: string;
  startedAt: string | null; elapsedMinutes: number | null; participants: LiveParticipant[];
}
export interface LiveSessions {
  generatedAt: string; connectedAgentsCount: number;
  connectedUserIds: string[]; sessions: LiveSession[];
}

export const forensicsApi = {
  get: (query: string, by?: string) =>
    request<ForensicsResponse>(
      'meeting',
      `/v1/diagnostics/forensics?query=${encodeURIComponent(query)}${by ? `&by=${encodeURIComponent(by)}` : ''}`,
    ),
  live: () => request<LiveSessions>('meeting', '/v1/diagnostics/forensics/live'),
};

async function requestUpload<T>(
  service: keyof typeof BASE_URLS,
  path: string,
  file: File,
): Promise<T> {
  const token = await getToken();
  const correlationId = crypto.randomUUID();
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${BASE_URLS[service]}${path}`, {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Correlation-Id': correlationId,
      ...(import.meta.env.DEV ? { 'X-Dev-Role': getDevRole() } : {}),
      // Sin 'Content-Type': el navegador lo fija con el boundary correcto de multipart/form-data.
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`API error ${status}`);
  }
}

/** Reintentos permitidos ante un fallo transitorio antes de darlo por error real. */
const TRANSIENT_ERROR_MAX_RETRIES = 2;

/**
 * Política de reintento para consultas sin refetch periódico (`retry: false` habitual)
 * que aun así deben sobrevivir dos tipos de fallo transitorio:
 *
 * - 401 por carrera de autenticación: MSAL (cuenta activa) o el SSO de Teams pueden tardar
 *   unos milisegundos más que el primer render, así que la primera petición sale con token
 *   vacío y recibe 401 aunque la sesión sea válida.
 * - 5xx por un servicio dependiente momentáneamente inalcanzable (p. ej. el pod de
 *   SurveyService reiniciando corta la llamada de AnalyticsService con "Connection
 *   refused", que el backend traduce a 500 — ver E-036 en errores-conocidos.md).
 *
 * Sin reintento, ambos casos quedan marcados como error permanente hasta que la persona
 * refresque manualmente. Cualquier otro código (404, 403, etc.) no reintenta: es un fallo real.
 */
export function retryOnTransientError(failureCount: number, error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const isTransient = error.status === 401 || error.status >= 500;
  return isTransient && failureCount < TRANSIENT_ERROR_MAX_RETRIES;
}

export const catalogApi = {
  getFundamentals: () => request<FundamentalCatalogResponse>('survey', '/v1/catalog/fundamentals'),

  // Variante para Reportería: incluye Fundamentales/Hábitos inactivos, necesarios para resolver
  // nombres históricos (Distribución por Fundamental, Heatmap, Top Reconocidos, etc.). NO usar en
  // el flujo de creación de reconocimientos — para eso está `getFundamentals()`.
  getFundamentalsIncludingInactive: () =>
    request<FundamentalCatalogResponse>('survey', '/v1/catalog/fundamentals?incluirInactivos=true'),

  createFundamental: (body: { names: Record<string, string>; shortNames?: Record<string, string>; shortName?: string; color?: string; descriptions?: Record<string, string>; description?: string; icon?: string; url?: string; micrositeUrl?: string }) =>
    request<FundamentalCatalogItem>('survey', '/v1/catalog/fundamentals', {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateFundamental: (id: string, body: { names: Record<string, string>; shortNames?: Record<string, string>; shortName?: string; color?: string; descriptions?: Record<string, string>; description?: string; icon?: string; url?: string; micrositeUrl?: string }) =>
    request<FundamentalCatalogItem>('survey', `/v1/catalog/fundamentals/${id}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteFundamental: (id: string) =>
    request<{ success: boolean }>('survey', `/v1/catalog/fundamentals/${id}`, { method: 'DELETE' }),

  createDailyHabit: (fundamentalId: string, body: { names: Record<string, string>; attitudeBehaviorId?: string }) =>
    request<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateDailyHabit: (fundamentalId: string, habitId: string, body: { names: Record<string, string> }) =>
    request<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteDailyHabit: (fundamentalId: string, habitId: string) =>
    request<{ success: boolean }>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}`, {
      method: 'DELETE',
    }),

  createAttitude: (fundamentalId: string, body: { names: Record<string, string>; descriptions?: Record<string, string> }) =>
    request<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes`, {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateAttitude: (fundamentalId: string, attitudeId: string, body: { names: Record<string, string>; descriptions?: Record<string, string> }) =>
    request<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}`, {
      method: 'PUT', body: JSON.stringify(body),
    }),
  deleteAttitude: (fundamentalId: string, attitudeId: string) =>
    request<{ success: boolean }>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}`, {
      method: 'DELETE',
    }),

  moveFundamental: (id: string, direction: 'up' | 'down') =>
    request<FundamentalCatalogItem>('survey', `/v1/catalog/fundamentals/${id}/move`, {
      method: 'PUT', body: JSON.stringify({ direction }),
    }),
  moveAttitude: (fundamentalId: string, attitudeId: string, direction: 'up' | 'down') =>
    request<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}/move`, {
      method: 'PUT', body: JSON.stringify({ direction }),
    }),
  moveDailyHabit: (fundamentalId: string, habitId: string, direction: 'up' | 'down') =>
    request<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}/move`, {
      method: 'PUT', body: JSON.stringify({ direction }),
    }),

  uploadFundamentalImage: (id: string, file: File) =>
    requestUpload<FundamentalCatalogItem>('survey', `/v1/catalog/fundamentals/${id}/image`, file),
  uploadAttitudeImage: (fundamentalId: string, attitudeId: string, file: File) =>
    requestUpload<AttitudeBehaviorCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/attitudes/${attitudeId}/image`, file),
  uploadDailyHabitImage: (fundamentalId: string, habitId: string, file: File) =>
    requestUpload<DailyHabitCatalogItem>('survey', `/v1/catalog/fundamentals/${fundamentalId}/habits/${habitId}/image`, file),
};

// Azure Translator resuelve el código 'pt' como portugués de Brasil (pt-br) por defecto;
// la app usa 'pt' para portugués de Portugal, así que se remapea a 'pt-pt' solo en la
// llamada al traductor. Ver locales/pt.ts y locales/recognition/pt.ts.
const AZURE_TRANSLATOR_LANG_OVERRIDES: Record<string, string> = {
  pt: 'pt-pt',
};

function toAzureTranslatorLangCode(lang: string): string {
  return AZURE_TRANSLATOR_LANG_OVERRIDES[lang] ?? lang;
}

// Utilidad genérica de traducción (Azure Translator) — reutilizable por cualquier pantalla
// que edite contenido multi-idioma, no exclusiva del builder de encuestas.
export const translationApi = {
  translateTexts: async (body: TranslateTextsRequest): Promise<TranslateTextsResponse> => {
    const azureTargetLangs = body.targetLangs.map(toAzureTranslatorLangCode);
    const response = await request<TranslateTextsResponse>('survey', '/v1/translate', {
      method: 'POST',
      body: JSON.stringify({
        ...body,
        sourceLang: toAzureTranslatorLangCode(body.sourceLang),
        targetLangs: azureTargetLangs,
      }),
    });
    const translations: Record<string, string[]> = {};
    body.targetLangs.forEach((lang, index) => {
      translations[lang] = response.translations[azureTargetLangs[index]] ?? response.translations[lang] ?? [];
    });
    return { translations };
  },
};

function formatPeriod(startDate?: string, expiresAt?: string): string {
  const fmt = (iso: string) => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };
  if (startDate && expiresAt) return `${fmt(startDate)} → ${fmt(expiresAt)}`;
  if (startDate) return `${fmt(startDate)} →`;
  if (expiresAt) return `→ ${fmt(expiresAt)}`;
  return '—';
}

function mapBackendSurveyStatus(backendStatus: string): SurveyItem['status'] {
  switch (backendStatus) {
    case 'Active':   return 'active';
    case 'Paused':   return 'paused';
    case 'Draft':    return 'pending_approval';
    case 'Cancelled': return 'cancelled';
    case 'Expired':
    case 'Archived':
    default:         return 'inactive';
  }
}

/**
 * Añade `?userId=` a una ruta cuando hay identidad que mandar (P4-7). Se extrae a función para
 * no anidar plantillas de cadena, que es lo que la regla `sonarjs/no-nested-template-literals`
 * marca en el resto de este archivo.
 */
function withUserId(path: string, userId?: string): string {
  if (!userId) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}userId=${encodeURIComponent(userId)}`;
}

async function fetchSurveys(status?: string): Promise<SurveyListResponse> {
  const path = status ? `/v1/surveys?status=${status}` : '/v1/surveys';
  const paged = await request<PagedSurveyResponse>('survey', path);
  return (paged.items ?? []).map(s => ({
    id: s.id,
    name: s.name,
    // `description` y `activeDays` los devuelve el backend desde siempre
    // (GetSurveysQueryHandler.cs:31,50) y este mapeo los descartaba. Los necesitan la descripción
    // bajo el nombre (P4-6) y la columna FRECUENCIA (D-3).
    description: s.description,
    activeDays: s.activeDays,
    status: mapBackendSurveyStatus(s.status),
    audience: s.ouIds?.join(', ') ?? '',
    ouIds: s.ouIds ?? [],
    questions: s.questions?.length ?? 0,
    responses: s.responseCount ?? 0,
    period: formatPeriod(s.startDate, s.expiresAt),
    periodStart: s.startDate,
    periodEnd: s.expiresAt,
    audienceType: s.audienceType,
    createdBy: s.createdBy,
    isApproved: s.isApproved,
    deletedAt: s.deletedAt,
    deletedBy: s.deletedBy,
    category: s.category,
  }));
}

export const surveyApi = {
  getSurveys:           ()                       => fetchSurveys(),
  getDeletedSurveys:    ()                       => fetchSurveys('Cancelled'),
  // Sin `tenantId` en el query string: el backend resuelve el tenant del claim JWT "tid" y
  // este parámetro nunca tuvo binding en el controlador (ver plan fix-tenantid-vs-region).
  getPendingInstances:  (userId?: string) =>
    request<PendingInstancesResponse>('survey', `/v1/survey-instances/pending${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
  getMyStats:           (userId?: string)         => request<MyStatsResponse>('survey', `/v1/survey-instances/my-stats${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
  /** Encuestas ya respondidas por el usuario, más recientes primero. */
  getMyAnswered:        (userId?: string, limit = 5) =>
    request<AnsweredInstance[]>('survey', `/v1/survey-instances/my-answered?limit=${limit}${userId ? `&userId=${encodeURIComponent(userId)}` : ''}`),
  submitResponse:       (instanceId: string, answers: Array<{ questionId: string; questionType: string; score: number | null; openText: string | null }>, userId?: string) =>
    request<SubmitResponseResult>('survey', `/v1/responses/${instanceId}${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`, { method: 'POST', body: JSON.stringify({ surveyInstanceId: instanceId, answers }) }),
  omitInstance:         (instanceId: string, userId?: string) =>
    request<{ success: boolean }>('survey', `/v1/responses/${instanceId}/omit${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`, { method: 'POST' }),
  // P4-7 — `userId` viaja para que el backend registre un `createdBy` real. El SurveyService lo
  // toma del claim `oid`, que en DEV/QA pone DevAuthMiddleware leyendo justamente `?userId=`
  // (SurveysController.cs:78). Sin el parámetro, toda encuesta nace con el usuario de relleno del
  // middleware y la tabla del panel la muestra como "Desconocido". Mismo patrón que
  // submitResponse/omitInstance.
  createSurvey:         (body: CreateSurveyRequest, userId?: string) =>
    request<CreateSurveyResponse>('survey', withUserId('/v1/surveys', userId), { method: 'POST', body: JSON.stringify(body) }),
  deleteSurvey:         (id: string)             => request<void>('survey', `/v1/surveys/${id}`, { method: 'DELETE' }),
  // Single survey (for edit mode)
  getSurveyById:        (id: string) => request<PagedSurveyDto>('survey', `/v1/surveys/${id}`),
  // Templates
  getTemplates:         (page = 1, pageSize = 20) =>
    request<PagedSurveyResponse>('survey', `/v1/surveys/templates?page=${page}&pageSize=${pageSize}`),
  duplicateTemplate:    (id: string)             =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/templates/${id}/duplicate`, { method: 'POST' }),
  deleteTemplate:       (id: string)             => request<void>('survey', `/v1/surveys/templates/${id}`, { method: 'DELETE' }),
  // Tips
  getTips:              (fundamentalId?: string) =>
    request<TipsResponse>('survey', `/v1/tips${fundamentalId ? `?fundamentalId=${fundamentalId}` : ''}`),
  createTip:            (body: Omit<TipItem, 'id'>) =>
    request<TipItem>('survey', '/v1/tips', { method: 'POST', body: JSON.stringify(body) }),
  updateTip:            (id: string, body: Omit<TipItem, 'id'>) =>
    request<TipItem>('survey', `/v1/tips/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTip:            (id: string)             => request<void>('survey', `/v1/tips/${id}`, { method: 'DELETE' }),
  // Actions
  getActions:           (fundamentalId?: string) =>
    request<ActionsResponse>('survey', `/v1/actions${fundamentalId ? `?fundamentalId=${fundamentalId}` : ''}`),
  createAction:         (body: Omit<ActionItem, 'id'>) =>
    request<ActionItem>('survey', '/v1/actions', { method: 'POST', body: JSON.stringify(body) }),
  updateAction:         (id: string, body: Omit<ActionItem, 'id'>) =>
    request<ActionItem>('survey', `/v1/actions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteAction:         (id: string)             => request<void>('survey', `/v1/actions/${id}`, { method: 'DELETE' }),
  // Survey CRUD extras
  updateSurvey: (id: string, body: { name: string; description?: string; category?: string; startDate?: string; expiresAt?: string; questions: unknown[]; campaignWeight?: number; activeDays?: number[]; boostUrgente?: boolean; boostDays?: number; minRespondents?: number; maxRespondents?: number; audienceType?: string; ouIds?: string[]; targetUserIds?: string[]; targetMeetingId?: string; nameTranslations?: Record<string, string>; descriptionTranslations?: Record<string, string> }, userId?: string) =>
    request<CreateSurveyResponse>('survey', withUserId(`/v1/surveys/${id}`, userId), { method: 'PUT', body: JSON.stringify(body) }),
  restoreSurvey: (id: string) =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}/restore`, { method: 'POST' }),
  permanentDeleteSurvey: (id: string) =>
    request<void>('survey', `/v1/surveys/${id}/permanent`, { method: 'DELETE' }),
  patchSurveyStatus: (id: string, action: 'activate' | 'pause') =>
    request<CreateSurveyResponse>('survey', `/v1/surveys/${id}/status`, { method: 'PATCH', body: JSON.stringify({ action }) }),
  createTemplate: (body: Omit<CreateSurveyRequest, 'isGlobalAdmin'>) =>
    request<CreateSurveyResponse>('survey', '/v1/surveys/templates', { method: 'POST', body: JSON.stringify(body) }),
  saveAsTemplate: (id: string, userId?: string) =>
    request<CreateSurveyResponse>('survey', withUserId(`/v1/surveys/${id}/save-as-template`, userId), { method: 'POST' }),
  duplicateSurvey: (id: string, userId?: string) =>
    request<CreateSurveyResponse>('survey', withUserId(`/v1/surveys/${id}/duplicate`, userId), { method: 'POST' }),
  exportSurvey: (id: string, lang = 'en') =>
    request<SurveyExportData>('survey', `/v1/surveys/${id}/export?lang=${encodeURIComponent(lang)}`),
  recalculateResponseCounts: () =>
    request<{ surveysUpdated: number }>('survey', '/v1/surveys/recalculate-responses', { method: 'POST' }),
};

export interface AnalyticsFilterParams {
  ouId?: string;
  /**
   * Alcance de OUs de la consulta, ya expandido con los descendientes de cada OU (el árbol lo
   * conoce el cliente vía UserAdminService, no AnalyticsService). El backend lo recibe como
   * lista separada por comas y filtra los participantes con `OuId $in`.
   */
  ouIds?: string[];
  startDate?: string; // ISO date string
  endDate?: string;   // ISO date string
  language?: string;  // BCP-47 base tag, e.g. "es", "en", "pt"
}

function buildAnalyticsQs(params: AnalyticsFilterParams): string {
  const parts: string[] = [];
  if (params.ouId)      parts.push(`ouId=${encodeURIComponent(params.ouId)}`);
  if (params.ouIds?.length) parts.push(`ouIds=${encodeURIComponent(params.ouIds.join(','))}`);
  if (params.startDate) parts.push(`startDate=${encodeURIComponent(params.startDate)}`);
  if (params.endDate)   parts.push(`endDate=${encodeURIComponent(params.endDate)}`);
  if (params.language)  parts.push(`lang=${encodeURIComponent(params.language)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

export const analyticsApi = {
  getSummary:      (filters: AnalyticsFilterParams = {}) => request<AnalyticsSummaryResponse>('analytics', `/v1/analytics/summary${buildAnalyticsQs(filters)}`),
  getFundamentals: (filters: AnalyticsFilterParams = {}) => request<FundamentalsResponse>('analytics', `/v1/analytics/fundamentals${buildAnalyticsQs(filters)}`),
  getHeatmap:      (filters: AnalyticsFilterParams = {}) => request<HeatmapResponse>('analytics', `/v1/analytics/heatmap${buildAnalyticsQs(filters)}`),
  getOna:          (filters: AnalyticsFilterParams = {}) => request<OnaResponse>('analytics', `/v1/analytics/ona${buildAnalyticsQs(filters)}`),
  getGenerational: (filters: AnalyticsFilterParams = {}) => request<GenerationalResponse>('analytics', `/v1/analytics/generational${buildAnalyticsQs(filters)}`),
  getGenerationalParticipants: (filters: AnalyticsFilterParams = {}) => request<GenerationalParticipantsResponse>('analytics', `/v1/analytics/generational/participants${buildAnalyticsQs(filters)}`),
  getBias:         (filters: AnalyticsFilterParams = {}) => request<BiasResponse>('analytics', `/v1/analytics/bias${buildAnalyticsQs(filters)}`),
  getOmissions:    (filters: AnalyticsFilterParams = {}) => request<OmissionsResponse>('analytics', `/v1/analytics/omissions${buildAnalyticsQs(filters)}`),
  getPersonal:       (userId?: string) => request<PersonalResponse>('analytics', `/v1/analytics/personal${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
  getPersonalGrowth: (userId?: string) => request<PersonalGrowthResponse>('analytics', `/v1/analytics/personal/growth${userId ? `?userId=${encodeURIComponent(userId)}` : ''}`),
};

export const userAdminApi = {
  // Sin `tenantId` en el query string: el backend resuelve el tenant del claim JWT "tid" y
  // este parámetro nunca tuvo binding en el controlador (ver plan fix-tenantid-vs-region).
  getUsers: ({ page = 1, pageSize = 20, ouId, search, roles, includeUnclaimed, isActive }: {
    page?: number;
    pageSize?: number;
    ouId?: string;
    search?: string;
    /** Nombres del enum UserRole del backend (ej. ['AdminGlobal', 'AdminOU']) — se envían como CSV. */
    roles?: string[];
    /**
     * D-U2 — fichas del padrón que su dueño todavía NO ha reclamado. **Ocultas por omisión**:
     * son 12 921 en QA y ahogarían el listado de administración, que es de personas con las
     * que se opera. El administrador las pide cuando las necesita.
     */
    includeUnclaimed?: boolean;
    /**
     * Sin especificar el backend NO filtra: administración tiene que poder ver a los inactivos para
     * gestionarlos. Los SELECTORES sí lo piden en `true` — a quien está de baja no se le reconoce ni
     * se le nombra administrador (mismo criterio que `GET /v1/users/search`).
     */
    isActive?: boolean;
  }) => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (ouId) params.set('ouId', ouId);
    if (search) params.set('search', search);
    if (isActive !== undefined) params.set('isActive', String(isActive));
    if (roles && roles.length > 0) params.set('roles', roles.join(','));
    // 🔴 SIEMPRE se manda, tambien cuando es false. El backend solo oculta las fichas sin
    // reclamar cuando recibe `false` EXPLICITO: si el parametro no llega, las INCLUYE. Como el
    // front nunca lo mandaba, D-U2 ("ocultas por omision") simplemente no estaba implementado, y
    // desde la carga del padron son 12 923 fichas colandose en todo listado que use este endpoint.
    params.set('includeUnclaimed', includeUnclaimed ? 'true' : 'false');
    return request<UserListResponse>('userAdmin', `/v1/users?${params.toString()}`);
  },
  getUser:    (id: string) => request<UserItem>('userAdmin', `/v1/users/${id}`),
  createUser: (body: CreateUserApiRequest) =>
    request<UserItem>('userAdmin', '/v1/users', { method: 'POST', body: JSON.stringify(body) }),
  updateUser: (id: string, body: { preferredLanguage?: string }) =>
    request<UserItem>('userAdmin', `/v1/users/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  updateUserOuScope: (id: string, body: { ouIds: string[]; allOusAccess: boolean }) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/ou-scope`, { method: 'PUT', body: JSON.stringify(body) }),
  /** Auto-servicio: el propio usuario cambia su idioma preferido (sin permisos de Admin). */
  updateOwnLanguage: (id: string, preferredLanguage: string) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/language`, {
      method: 'PATCH', body: JSON.stringify({ preferredLanguage }),
    }),
  toggleUserActive: (id: string, isActive: boolean) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/active`, { method: 'PATCH', body: JSON.stringify({ isActive }) }),
  updateUserRoles: (id: string, roles: string[]) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/roles`, { method: 'PUT', body: JSON.stringify({ roles }) }),
  updateUserPermissions: (id: string, perms: AdminPermissions) =>
    request<UserItem>('userAdmin', `/v1/users/${id}/permissions`, { method: 'PATCH', body: JSON.stringify(perms) }),
  updateUserModules: (userId: string, body: UserModules) =>
    request<UserItem>('userAdmin', `/v1/users/${userId}/modules`, {
      method: 'PATCH', body: JSON.stringify(body),
    }),
  deleteUser: (id: string) => request<void>('userAdmin', `/v1/users/${id}`, { method: 'DELETE' }),
  // Sin `tenantId` en el query string: el backend resuelve el tenant del claim JWT "tid" y
  // este parámetro nunca tuvo binding en el controlador (ver plan fix-tenantid-vs-region).
  getOUs:       () => request<OUListResponse>('userAdmin', '/v1/organizational-units'),
  getOUTree:    (ouId: string)    => request<OUTreeResponse>('userAdmin', `/v1/organizational-units/${ouId}/tree`),
  getOURoots:   ()                => request<OURootsResponse>('userAdmin', '/v1/organizational-units/roots'),
  getOuCatalogCacheStatus: ()     => request<OuCatalogCacheStatusDto>('userAdmin', '/v1/organizational-units/cache-status'),
  getJobs: () => request<JobsListResponse>('userAdmin', '/v1/jobs'),
  getJobRuns: (jobName: JobSlug, limit = 30) =>
    request<JobRunsListResponse>('userAdmin', `/v1/jobs/${jobName}/runs?limit=${limit}`),
  triggerJob: (jobName: JobSlug) =>
    request<JobRunDto>('userAdmin', `/v1/jobs/${jobName}/run`, { method: 'POST' }),
  createOU: (body: { name: string; description?: string; type?: string; gdpr?: boolean; timezone?: string; azureAdGroupId?: string }) =>
    request<OUItem>('userAdmin', '/v1/organizational-units', { method: 'POST', body: JSON.stringify(body) }),
  updateOU: (ouId: string, body: { name?: string; description?: string; type?: string; gdpr?: boolean; timezone?: string; azureAdGroupId?: string }) =>
    request<OUItem>('userAdmin', `/v1/organizational-units/${ouId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteOU: (ouId: string) =>
    request<void>('userAdmin', `/v1/organizational-units/${ouId}`, { method: 'DELETE' }),
  setOuActive:  (ouId: string, isActive: boolean) =>
    request<OUItem>('userAdmin', `/v1/organizational-units/${ouId}/active`, {
      method: 'PATCH', body: JSON.stringify({ isActive }),
    }),
  /** Resuelve displayName en lote para una lista de userIds. Usado en flujo de meetings. */
  // El cuerpo usa `ids`, que es el nombre del campo en BatchLookupRequest (UserAdminService).
  // Enviarlo como `userIds` dejaba la lista nula en el servidor y el endpoint respondía sin
  // ningún usuario, así que el ranking generacional mostraba identificadores en vez de nombres.
  batchLookupUsers: (userIds: string[]) =>
    request<Array<{ id: string; displayName: string }>>('userAdmin', '/v1/users/batch-lookup', {
      method: 'POST', body: JSON.stringify({ ids: userIds }),
    }),
};

export const rulesApi = {
  getRules:   ()                           => request<RulesListResponse>('rules', '/v1/rules'),
  updateRule: (code: string, body: Omit<RuleItem, 'code'>) =>
    request<RuleItem>('rules', `/v1/rules/${code}`, { method: 'PUT', body: JSON.stringify(body) }),
  upsertRuleOuOverride: (code: string, ouId: string, value: string | null) =>
    request<RuleItem>('rules', `/v1/rules/${code}/ou-overrides/${encodeURIComponent(ouId)}`, {
      method: 'PATCH', body: JSON.stringify({ value }),
    }),
};

export interface SaveOmitirSettingsRequest {
  omitirEnabled: boolean;
  omitirThreshold: number;
  omitirNotifyUser: boolean;
  omitirNotifyManager: boolean;
  omitirNotifyAdminOU: boolean;
  omitirUserTemplates: Record<string, string>;
  omitirMgrTemplates: Record<string, string>;
  omitirOuOverrides: Array<{ ouId: string; language: string; userMessage: string; managerMessage: string }>;
}

export interface SaveEuropaSettingsRequest {
  europaEnabled: boolean;
  europaUserTemplates: Record<string, string>;
  europaMgrTemplates: Record<string, string>;
}

export const notificationsApi = {
  getSettings: () =>
    request<NotificationSettingsDto>('notifications', '/v1/notifications/settings'),
  saveSettings: (body: NotificationSettingsDto) =>
    request<void>('notifications', '/v1/notifications/settings', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  saveOmitirSettings: (body: SaveOmitirSettingsRequest) =>
    request<void>('notifications', '/v1/notifications/settings/omitir', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  saveEuropaSettings: (body: SaveEuropaSettingsRequest) =>
    request<void>('notifications', '/v1/notifications/settings/europa', {
      method: 'PUT', body: JSON.stringify(body),
    }),
  sendGeneral: (body: SendGeneralNotificationRequest) =>
    request<SendGeneralNotificationResult>('notifications', '/v1/notifications/general', {
      method: 'POST', body: JSON.stringify(body),
    }),
};

/** Perfil resuelto del usuario autenticado (roles, idioma, permisos y alcance de OUs). */
export interface CurrentUserProfile {
  roles: string[];
  /**
   * Identificador de la FICHA en el padrón, tal cual lo devuelve `/v1/users/resolve`.
   *
   * 🔴 NO es el mismo que el `oid` de Entra con el que la persona inició sesión. Coinciden sólo
   * cuando su ficha nació del directorio corporativo; para el padrón cargado por SuccessFactors o
   * por el Excel de Europa/EE. UU. la ficha lleva un identificador propio.
   *
   * Es el que el backend usa desde el arreglo del ticket 222 para firmar quién envía un
   * reconocimiento. La web tiene que compararse contra ÉSTE y no contra el `oid`, o la persona
   * deja de reconocerse como dueña de lo suyo: no puede borrar ni editar sus publicaciones y su
   * lista de «Enviados» sale vacía.
   *
   * `undefined` mientras el perfil no ha resuelto, o si el backend no lo devolvió.
   */
  fichaId?: string;
  /**
   * 'resolved' = el backend devolvió un perfil real. 'not-found' = /v1/users/resolve respondió
   * 404 explícito: la persona se autenticó por AD pero todavía no existe en Mongo. 'error' =
   * cualquier otro fallo (red, 5xx, timeout) — no se sabe si el perfil existe o no, se trata
   * distinto de 'not-found' porque no es seguro asumir que la persona no está dada de alta.
   *
   * 'pending-hire-date' (REQ 3) = el perfil EXISTE pero su fecha de ingreso es futura, así que
   * todavía no puede entrar. Lo decide el backend (`UserDto.accessPendingHireDate`) y no la web: el
   * endpoint es anónimo, así que devuelve la DECISIÓN y no la fecha — publicar la fecha permitiría
   * averiguar la de cualquiera con sólo su correo.
   */
  profileStatus: 'resolved' | 'not-found' | 'error' | 'pending-hire-date';
  preferredLanguage?: string;
  givenName?: string;
  permissions: AdminPermissions;
  ouScope: OuScope;
  /**
   * Alcance organizacional sobre Reconocimiento/Brillamos (`UserDto.RecognitionScopeOuIds`), ya
   * expandido con descendencia. Distinto de `ouScope`, que es el alcance de Pulso: un
   * AdminOUBrillamos no administra Pulso, así que las pantallas de campañas/puntos deben leer este
   * campo y no `ouScope` — mezclarlos deja el alcance vacío para un admin que solo tiene el rol de
   * Brillamos (fix 2026-08-12).
   */
  recognitionOuScope: OuScope;
  modules: UserModules;
  /**
   * Identidad organizacional del colaborador. Sale del MISMO `UserDto` que ya se pedía
   * (`GET v1/users/resolve`) y hasta ahora se descartaba al construir este perfil: por eso la
   * tarjeta del Perfil salía sin puesto, sin área y sin OU aunque el directorio sí los tuviera
   * (P3-2). `ouId` es la clave que viaja a las APIs; `ouName` es la etiqueta que se muestra.
   */
  jobTitle?: string;
  ouId?: string;
  ouName?: string;
  department?: string;
  /** Foto de AD/Entra ID (ver `UserItem.photoUrl` — PENDIENTE de backend, hoy siempre undefined). */
  photoUrl?: string | null;
  /**
   * Permisos canónicos efectivos del módulo Reconocimiento (`UserDto.Permissions`, 20 claves de
   * `PermissionKeys.Order`), o null si no se pudo resolver. Gatea las 12 secciones del Admin de
   * Fundamentales — ver `getAccessibleRecognitionSections` en `contexts/UserContext.tsx`.
   */
  recognitionPermissions: string[] | null;
}

const NO_PERMISSIONS: AdminPermissions = {
  canCreateSurveys: false,
  canViewAnalytics: false,
  canManageUsers: false,
  canApproveSurveys: false,
  canAccessCatalogFundamentals: false,
  canAccessCatalogOus: false,
};

/** Sin perfil resuelto no hay alcance: ninguna OU raíz y sin acceso global. */
const NO_OU_SCOPE: OuScope = { rootOuIds: [], allOusAccess: false };

/**
 * Sin perfil resuelto (mock/sin correo, o falla la llamada): ningún módulo.
 *
 * 🔴 Antes era `canViewPulso: true` ("es lo que tienen todos los usuarios de AD") — supuesto
 * obsoleto: el negocio confirmó que `CanViewPulso` debe ser `false` para todos mientras Pulso
 * siga en piloto (ver CHANGELOG de UserAdminService-api). Con el valor viejo, cualquier falla de
 * red al resolver el perfil dejaba a la persona con Pulso visible de forma permanente hasta
 * recargar — el mismo supuesto causaba además un parpadeo del menú en `useCurrentUser` mientras
 * el perfil real cargaba (fila 112, 2026-09-04).
 */
const DEFAULT_MODULES: UserModules = { canViewPulso: false, canViewBrillamos: false };

function toOuScope(scopeOuIds: string[] | undefined, allOusAccess: boolean | undefined): OuScope {
  if (allOusAccess) return { rootOuIds: [], allOusAccess: true };
  return { rootOuIds: scopeOuIds ?? [], allOusAccess: false };
}

/**
 * BUG (2026-09-05) — `useCurrentUser.ts` usaba `useState`/`useEffect` LOCAL a cada componente que lo
 * llama, sin caché: `AdminView` (ruta hoja) se desmonta/remonta al salir y volver a `/app/admin`, y
 * cada reingreso disparaba un `getCurrentUserProfile` nuevo e independiente — que dentro del iframe
 * de Teams (con su propio intercambio de token SSO) fallaba con más frecuencia que en navegador
 * normal, degradando el perfil a Employee y redirigiendo fuera de Administración sin ningún error
 * visible. El primer fix migró `useCurrentUser` a `useQuery` (TanStack Query) para compartir un solo
 * fetch por correo — RESULTÓ ser la causa de un OOM real en CI (ver docs/contexto/errores-conocidos.md):
 * con `useCurrentUser` exigido por `Sidebar`/`AppRouter`/`UserProvider`, cientos de archivos de test
 * que antes no tocaban TanStack Query para nada empezaron a hacerlo, y el proceso de Vitest (que
 * reutiliza el mismo worker a lo largo de cientos de archivos sin reiniciarlo) acumuló memoria hasta
 * reventar el heap. Se revirtió esa migración.
 *
 * Este caché resuelve el MISMO bug (un remontaje no debe volver a arriesgar el fetch) sin ninguna
 * librería: una variable de módulo, sin `QueryClient`, sin observers, sin costo por archivo de test.
 * Solo cachea un ÉXITO — un fallo NUNCA sobrescribe un valor bueno ya cacheado (si lo hiciera, el
 * primer parpadeo del SSO de Teams degradaría a Employee para SIEMPRE hasta el próximo F5, en vez de
 * solo para ese remontaje puntual — exactamente el bug original). Se limpia solo al cambiar de
 * correo: login y logout en esta app SIEMPRE hacen una carga completa de página (`window.location.
 * assign`, ver LoginView.tsx/MobileNav.tsx), así que el módulo se reinicia solo en cada sesión nueva
 * — no hace falta invalidarlo a mano en producción. `resetCurrentUserProfileCacheForTests` es SOLO
 * para pruebas que llaman a esta función directamente (sin mockear `@/api/client`) con el MISMO
 * correo esperando respuestas distintas — ver `getCurrentUserProfile.test.ts`/`client.test.ts`.
 */
let cachedProfile: { email: string; profile: CurrentUserProfile } | null = null;

/** Solo para pruebas — ver el comentario de arriba. */
export function resetCurrentUserProfileCacheForTests(): void {
  cachedProfile = null;
}

/**
 * Perfil (roles + idioma preferido + permisos) del usuario actual, tomado de UserAdminService
 * (gestionado en la app, NO en Azure AD salvo por la sincronización inicial de Graph).
 * `ResolveByEmail` devuelve el UserDto con roles, preferredLanguage y los cuatro permisos
 * granulares que controlan qué secciones del panel ve un AdminOU.
 * Fail-soft: devuelve roles=[], preferredLanguage=undefined y todos los permisos en false si no
 * se puede resolver (usuario no existe / servicio caído) → el portal lo trata como Employee.
 */
export async function getCurrentUserProfile(email: string): Promise<CurrentUserProfile> {
  if (USE_MOCK || !email) {
    return {
      roles: [],
      profileStatus: 'resolved',
      permissions: { ...NO_PERMISSIONS },
      ouScope: { ...NO_OU_SCOPE },
      recognitionOuScope: { ...NO_OU_SCOPE },
      modules: { ...DEFAULT_MODULES },
      recognitionPermissions: null,
    };
  }
  if (cachedProfile?.email === email) return cachedProfile.profile;
  const profile = await fetchCurrentUserProfileUncached(email);
  // Solo un perfil que el backend SÍ resolvió es un valor "bueno" digno de cachear — 'error' es la
  // señal fail-soft de un fetch que falló, no un dato real del usuario.
  if (profile.profileStatus !== 'error') cachedProfile = { email, profile };
  return profile;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * BUG (2026-09-07) — reportado como "funciona a la segunda": la primera vez que un admin
 * legítimo entraba a Administración lo rebotaba a Reconocer; un segundo clic sí funcionaba.
 * Reproducido LOCAL, determinista, sin Teams de por medio (repro: cuenta admin real, primer
 * intento a `/app/admin` falla, segundo intento resuelve) — no depende del SSO de Teams
 * específicamente, es un 401/5xx transitorio genérico (token todavía no listo, servicio
 * dependiente reiniciando) tratado exactamente igual que "este usuario no es admin".
 *
 * `retryOnTransientError` ya existe para este patrón EXACTO (401 vacío / 5xx momentáneo — ver su
 * comentario) y ya lo usan `useAnalytics`/`useMyAnswered`/`usePersonalGrowth` vía `useQuery`. Esta
 * función no puede usar `useQuery` (ver el comentario de `getCurrentUserProfile` — es la causa
 * confirmada del OOM de CI), así que reintenta el mismo criterio a mano, antes de fail-softear a
 * un perfil de error — que hoy AdminView/canAccessView no distinguen de "esta persona no es
 * admin", y por eso un solo fallo transitorio bastaba para expulsarla del panel.
 */
// 🔴 DIAGNÓSTICO TEMPORAL (2026-09-08) — id incremental para distinguir, en consola, las varias
// llamadas CONCURRENTES a getCurrentUserProfile (cada componente que llama useCurrentUser() dispara
// la suya, sin compartir la de los demás hasta que una resuelve). Quitar junto con los console.error
// de abajo en cuanto se confirme la causa — ver docs/contexto/errores-conocidos.md.
let diagCallCounter = 0;

async function fetchCurrentUserProfileUncached(email: string): Promise<CurrentUserProfile> {
  const diagId = ++diagCallCounter;
  let lastError: unknown;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchCurrentUserProfileOnce(email, diagId);
    } catch (error) {
      lastError = error;
      console.error('[DIAG profile]', diagId, 'intento', attempt, 'fallo', error);
      if (!retryOnTransientError(attempt, error)) break;
      await delay(300 * (attempt + 1));
    }
  }
  console.error('[DIAG profile]', diagId, 'AGOTADO — cae a Employee/error', lastError);
  const profileStatus = lastError instanceof ApiError && lastError.status === 404 ? 'not-found' : 'error';
  return {
    roles: [],
    profileStatus,
    permissions: { ...NO_PERMISSIONS },
    ouScope: { ...NO_OU_SCOPE },
    recognitionOuScope: { ...NO_OU_SCOPE },
    modules: { ...DEFAULT_MODULES },
    recognitionPermissions: null,
  };
}

async function fetchCurrentUserProfileOnce(email: string, diagId?: number): Promise<CurrentUserProfile> {
  const dto = await request<Partial<UserItem> & { givenName?: string }>(
    'userAdmin',
    `/v1/users/resolve?email=${encodeURIComponent(email)}`,
  );
  console.error('[DIAG profile]', diagId, 'dto crudo recibido', {
    roles: dto?.roles, canViewBrillamos: dto?.canViewBrillamos, canViewPulso: dto?.canViewPulso,
    permissionsLen: dto?.permissions?.length, dtoIsNullish: dto == null,
  });
  return {
    roles: dto?.roles ?? [],
    // El identificador de la ficha. Ya venía en el DTO y se descartaba: es justo el dato con el que
    // el backend firma los reconocimientos, así que sin él la web compara contra el `oid` y no
    // reconoce como propio lo que sí lo es.
    fichaId: dto?.id,
    // REQ 3 — el perfil se resolvio, pero si su ingreso es futuro todavia no puede entrar. Se
    // distingue de 'not-found' a proposito: la persona SI existe, y confundir las dos cosas haria
    // imposible saber por que esta gateada.
    profileStatus: dto?.accessPendingHireDate === true ? 'pending-hire-date' : 'resolved',
    preferredLanguage: dto?.preferredLanguage,
    givenName: dto?.givenName,
    jobTitle: dto?.jobTitle,
    ouId: dto?.ouId,
    ouName: dto?.ouName,
    department: dto?.department,
    photoUrl: dto?.photoUrl,
    permissions: {
      canCreateSurveys:  dto?.canCreateSurveys  ?? false,
      canViewAnalytics:  dto?.canViewAnalytics  ?? false,
      canManageUsers:    dto?.canManageUsers    ?? false,
      canApproveSurveys: dto?.canApproveSurveys ?? false,
      canAccessCatalogFundamentals: dto?.canAccessCatalogFundamentals ?? false,
      canAccessCatalogOus:          dto?.canAccessCatalogOus          ?? false,
    },
    modules: {
      canViewPulso: dto?.canViewPulso ?? DEFAULT_MODULES.canViewPulso,
      canViewBrillamos: dto?.canViewBrillamos ?? DEFAULT_MODULES.canViewBrillamos,
    },
    ouScope: toOuScope(dto?.pulsoAdminScopeOuIds, dto?.allOusAccess),
    // 🔴 Bug UAT #114 — `allOusAccess` también manda aquí. Antes se pasaba `false` fijo, con el
    // razonamiento de que los roles de alcance global no pasan por el selector de OU y el caso
    // "sin restricción" lo resolvía `useCurrentUser` por rol. Pero la marca «acceso a todas las
    // OUs» se le puede poner a un admin ACOTADO (es justo lo que hizo la sesión de UAT para
    // destrabarlo): ese actor no cae por la rama de rol, se quedaba con `rootOuIds: []` y el
    // árbol de Alcance de campañas salía VACÍO. El backend ya trata esa marca como alcance de
    // tenant en Reconocimiento (`RecognitionAccess.From`: `allOusAccess` ⇒ AdminGlobal); esto
    // hace que la UI diga lo mismo.
    recognitionOuScope: toOuScope(dto?.recognitionScopeOuIds, dto?.allOusAccess),
    recognitionPermissions: dto?.permissions ?? null,
  };
}

export const traceApi = {
  /**
   * Traza del pipeline de una reunión. Solo la consume la pestaña de Trazabilidad (AdminGlobal).
   * Sin `tenantId` en el query string: el backend lo resuelve del token del llamador e ignora
   * cualquier valor recibido por query (cierre de fuga de datos entre tenants).
   */
  getTrace: (params: TraceQueryParams): Promise<TraceResponse> => {
    const query = new URLSearchParams();
    if (params.meetingId) query.set('meetingId', params.meetingId);
    if (params.email) query.set('email', params.email);
    if (params.meetingSubject) query.set('meetingSubject', params.meetingSubject);
    const queryString = query.toString();
    const path = queryString ? `/v1/diagnostics/trace?${queryString}` : '/v1/diagnostics/trace';
    return request<TraceResponse>('meeting', path);
  },
};
